import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Pencil, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SponsorsTab({ api }) {
  const [sponsors, setSponsors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", link: "", sort_order: 0 });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/sponsors").then(r => setSponsors(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, logo_url: r.data.url }));
      toast.success("Logo hochgeladen");
    } catch { toast.error("Upload fehlgeschlagen"); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name || !form.logo_url) { toast.error("Name und Logo sind Pflichtfelder"); return; }
    try {
      if (editing) {
        await api.put(`/sponsors/${editing}`, form);
        toast.success("Sponsor aktualisiert");
      } else {
        await api.post("/sponsors", { ...form, sort_order: sponsors.length });
        toast.success("Sponsor hinzugefügt");
      }
      setEditing(null);
      setForm({ name: "", logo_url: "", link: "", sort_order: 0 });
      load();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sponsor wirklich löschen?")) return;
    try {
      await api.delete(`/sponsors/${id}`);
      toast.success("Sponsor gelöscht");
      load();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, logo_url: s.logo_url, link: s.link || "", sort_order: s.sort_order || 0 });
  };

  return (
    <div data-testid="sponsors-tab">
      <h2 className="text-xl font-bold uppercase tracking-tight mb-6" style={{ fontFamily: 'Oswald' }}>Sponsoren verwalten</h2>

      <div className="bg-card border border-white/5 p-5 mb-6" data-testid="sponsor-form">
        <h3 className="text-sm font-bold uppercase tracking-wider mb-4">{editing ? "Sponsor bearbeiten" : "Neuer Sponsor"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase">Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-input/50 border-white/10 rounded-none" placeholder="Sponsorname"
              data-testid="sponsor-name-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Website-Link</Label>
            <Input value={form.link} onChange={e => setForm({ ...form, link: e.target.value })}
              className="mt-1 bg-input/50 border-white/10 rounded-none" placeholder="https://..."
              data-testid="sponsor-link-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Reihenfolge</Label>
            <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              className="mt-1 bg-input/50 border-white/10 rounded-none"
              data-testid="sponsor-order-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Logo *</Label>
            <div className="mt-1 flex items-center gap-3">
              <label className="cursor-pointer px-3 py-2 text-xs font-bold uppercase border border-white/10 hover:bg-white/5 flex items-center gap-1">
                <Upload className="w-3 h-3" /> {uploading ? "Lädt..." : "Logo hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload}
                  data-testid="sponsor-logo-upload" />
              </label>
              {form.logo_url && <img src={form.logo_url.startsWith("/") ? `${API.replace('/api','')}${form.logo_url}` : form.logo_url} alt="" className="h-10 object-contain bg-white/10 px-2" />}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="sponsor-save-btn"
            className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider hover:-translate-y-0.5 transition-transform">
            {editing ? "Aktualisieren" : "Hinzufügen"}
          </button>
          {editing && (
            <button onClick={() => { setEditing(null); setForm({ name: "", logo_url: "", link: "", sort_order: 0 }); }}
              className="px-4 py-2 text-xs font-bold uppercase border border-white/10 hover:bg-white/5">
              Abbrechen
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {sponsors.map(s => (
          <div key={s.id} className="bg-card border border-white/5 p-4 flex items-center gap-4" data-testid={`sponsor-item-${s.id}`}>
            <img src={s.logo_url.startsWith("/") ? `${API.replace('/api','')}${s.logo_url}` : s.logo_url}
              alt={s.name} className="h-12 w-20 object-contain bg-white/10 rounded px-2" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{s.name}</p>
              {s.link && <a href={s.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline truncate block">{s.link}</a>}
            </div>
            <span className="text-xs text-muted-foreground">#{s.sort_order}</span>
            <button onClick={() => startEdit(s)} className="px-2 py-1 text-xs border border-white/10 hover:bg-white/5" data-testid={`sponsor-edit-${s.id}`}>
              <Pencil className="w-3 h-3" />
            </button>
            <button onClick={() => remove(s.id)} className="px-2 py-1 text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`sponsor-delete-${s.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {sponsors.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Sponsoren hinzugefügt.</p>}
      </div>
    </div>
  );
}
